import { getCityArray, getData, getDistrictArray } from '@simoko/tw-zip';
import { useCallback, useMemo, useState } from 'react';

// Assumption: @simoko/tw-zip always provides non-empty Taiwan city/district data.
// This is a static dataset package, so data/cities are guaranteed to be populated.
const data = getData();
const cities = getCityArray();

export function useTwZip({
  city: initialCity,
  district: initialDistrict,
}: {
  city: string;
  district: string;
}) {
  const [{ city, district }, setValue] = useState<{
    city: string;
    district: string;
  }>(() => {
    return data[initialCity]?.[initialDistrict]
      ? {
          city: initialCity,
          district: initialDistrict,
        }
      : {
          city: cities[0],
          district: getDistrictArray(cities[0])[0].label,
        };
  });
  const districts = useMemo(() => getDistrictArray(city), [city]);

  const reset = useCallback(() => {
    setValue(
      data[initialCity]?.[initialDistrict]
        ? { city: initialCity, district: initialDistrict }
        : { city: cities[0], district: getDistrictArray(cities[0])[0].label }
    );
  }, [initialCity, initialDistrict]);

  const setCity = useCallback((newCity: string) => {
    setValue({
      city: newCity,
      district: getDistrictArray(newCity)[0].label,
    });
  }, []);

  const setDistrict = useCallback((newDistrict: string) => {
    setValue((prev) => ({
      ...prev,
      district: newDistrict,
    }));
  }, []);

  return {
    cities,
    districts,
    city,
    setCity,
    district,
    setDistrict,
    // No null check needed: city/district are always valid (initialized from
    // validated input or defaults to first available city/district)
    zipCode: data[city][district],
    reset,
  };
}
